import { useEffect } from 'react';
import { X, Plus, Sparkles, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Sidebar } from '@/components/layout/Sidebar';
import { PlaygroundRouter } from '@/components/playground/PlaygroundRouter';
import { usePlaygroundStore } from '@/store/playground';
import { useModels } from '@/hooks/useModels';
import { cn } from '@/lib/utils';
import { parseShareUrl } from '@/lib/utils';
import { historyApi } from '@/services/api';

const TASK_COLORS: Record<string, string> = {
  'text-generation': 'blue',
  'text-to-image': 'purple',
  'image-to-text': 'orange',
  'automatic-speech-recognition': 'green',
  translation: 'blue',
  'text-embeddings': 'orange',
  'text-classification': 'green',
  'image-classification': 'purple',
  'object-detection': 'orange',
  summarization: 'green',
};

export default function App() {
  const { tabs, activeTabId, closeTab, setActiveTab, duplicateTab, openTab, setHistory } = usePlaygroundStore();
  const { data: modelsData } = useModels();

  // Load history from backend on mount
  useEffect(() => {
    historyApi.list({ limit: 100 }).then((res) => setHistory(res.entries)).catch(console.error);
  }, [setHistory]);

  // Handle share URL on load
  useEffect(() => {
    const shared = parseShareUrl();
    if (shared && modelsData) {
      const model = modelsData.models.find((m) => m.id === shared.modelId);
      if (model) {
        openTab(model);
      }
    }
  }, [modelsData, openTab]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Ctrl+W: close active tab
      if (e.ctrlKey && e.key === 'w') {
        e.preventDefault();
        if (activeTabId) closeTab(activeTabId);
      }
      // Ctrl+D: duplicate active tab
      if (e.ctrlKey && e.key === 'd') {
        e.preventDefault();
        if (activeTabId) duplicateTab(activeTabId);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeTabId, closeTab, duplicateTab]);

  const activeTab = tabs.find((t) => t.id === activeTabId);

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden">
      {/* Sidebar */}
      <Sidebar />

      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Tab bar */}
        {tabs.length > 0 && (
          <div className="flex items-center border-b border-border bg-card overflow-hidden">
            <ScrollArea className="flex-1" type="scroll">
              <div className="flex items-center gap-0 px-1 py-1 min-w-0">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'group flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all shrink-0 max-w-[200px] border',
                      tab.id === activeTabId
                        ? 'bg-background text-foreground border-border shadow-sm'
                        : 'text-muted-foreground hover:text-foreground border-transparent hover:bg-accent',
                    )}
                  >
                    <span className="truncate">{tab.modelName}</span>
                    {tab.isLoading && (
                      <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse shrink-0" />
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); closeTab(tab.id); }}
                      className="shrink-0 opacity-0 group-hover:opacity-100 hover:bg-destructive/20 rounded p-0.5 transition-all"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Active tab header */}
        {activeTab && (
          <div className="flex items-center gap-3 px-4 py-2 border-b border-border bg-card/50">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <h1 className="text-sm font-semibold truncate">{activeTab.modelName}</h1>
              <Badge variant={TASK_COLORS[activeTab.task] as 'blue' | 'orange' | 'green' | 'purple' | 'default' ?? 'secondary'} className="text-xs shrink-0">
                {activeTab.task}
              </Badge>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7"
                onClick={() => duplicateTab(activeTab.id)}
                title="Duplicate tab (Ctrl+D)"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Duplicate
              </Button>
            </div>
          </div>
        )}

        {/* Playground area */}
        <div className="flex-1 overflow-hidden">
          {activeTab ? (
            <PlaygroundRouter tab={activeTab} />
          ) : (
            <EmptyState onOpenModels={() => usePlaygroundStore.getState().setSidebarOpen(true)} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ onOpenModels }: { onOpenModels: () => void }) {
  const { data: modelsData } = useModels();
  const { openTab } = usePlaygroundStore();

  const featured = modelsData?.models.filter((m) => m.featured) ?? [];

  return (
    <div className="flex flex-col items-center justify-center h-full gap-8 p-8">
      <div className="text-center space-y-3">
        <div className="flex items-center justify-center gap-2">
          <Sparkles className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">AI Playground</h1>
        </div>
        <p className="text-muted-foreground max-w-md">
          Explore and test all Cloudflare Workers AI models. Select a model from the sidebar or choose one below to get started.
        </p>
      </div>

      {/* Featured models */}
      {featured.length > 0 && (
        <div className="w-full max-w-3xl">
          <h2 className="text-sm font-semibold text-muted-foreground mb-3">Featured models</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {featured.slice(0, 6).map((model) => (
              <button
                key={model.id}
                onClick={() => openTab(model)}
                className="text-left p-3 rounded-lg border border-border hover:bg-accent hover:border-primary/50 transition-all group"
              >
                <div className="flex items-start gap-2">
                  <span className="text-xl mt-0.5">
                    {model.task === 'text-generation' ? '💬' :
                     model.task === 'text-to-image' ? '🎨' :
                     model.task === 'image-to-text' ? '👁' :
                     model.task === 'automatic-speech-recognition' ? '🎙' :
                     model.task === 'translation' ? '🌐' : '🔢'}
                  </span>
                  <div>
                    <p className="text-sm font-medium group-hover:text-primary transition-colors">{model.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{model.author}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button onClick={onOpenModels}>
          <Sparkles className="h-4 w-4 mr-1" /> Browse all models
        </Button>
      </div>

      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <Info className="h-3.5 w-3.5" />
        <span>Powered by Cloudflare Workers AI · Tip: Press Ctrl+W to close a tab</span>
      </div>
    </div>
  );
}
